import { useState, useEffect, useCallback } from 'react';
import PageHeader from '../../components/PageHeader';
import { useFeature } from '../../contexts/FeatureContext';
import EbuyProfilePanel from '../../components/sentinel/EbuyProfilePanel';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Settings, 
  Users,
  UserPlus,
  Mail,
  Building2,
  BookOpen,
  Trash2,
  Edit2,
  Briefcase,
  CheckCircle2,
  Clock,
  XCircle,
  RefreshCw,
  Loader2,
  Search,
  Link2,
  Plug,
  Key,
  Eye,
  EyeOff,
  ExternalLink,
  AlertCircle,
  Radar,
  X,
  Plus,
  Target,
  Hash,
  FileText,
  Zap,
  Sparkles
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import SlotTopUpModal from '../../components/modals/SlotTopUpModal';
import CustomerPortalButton from '../../components/CustomerPortalButton';
// Reuse the BYOM card from the Consultancy settings — same backend
// endpoints, same shape, just mounted in the Contractor's settings tab.
import { OrgAIConfigurationCard } from '../consultant/ConsultancySettings';
import OrgCompanyProfile from '../../components/org/OrgCompanyProfile';
import OrgLibrary from '../../components/org/OrgLibrary';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WorkspaceSettings() {
  const { authToken } = useAuth();
  // SAM.gov integration powers Marketplace Sentinel. When the Sentinel
  // feature is disabled in production, the entire Integrations tab becomes
  // dead weight, so we hide it from the tab strip + content region below.
  const sentinelEnabled = useFeature('sentinel');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [organization, setOrganization] = useState(null);
  const [team, setTeam] = useState([]);
  const [linkedConsultant, setLinkedConsultant] = useState(null);
  const [isSlotTopUpOpen, setIsSlotTopUpOpen] = useState(false);
  
  // Consultant search state
  const [consultantSearch, setConsultantSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  
  // SAM.gov Integration state
  const [samGovStatus, setSamGovStatus] = useState(null);
  const [samGovApiKey, setSamGovApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [isSavingApiKey, setIsSavingApiKey] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  
  // Sentinel Preferences state
  const [sentinelPrefs, setSentinelPrefs] = useState({
    target_naics_codes: [],
    target_psc_codes: [],
    preferred_agencies: []
  });
  const [naicsInput, setNaicsInput] = useState('');
  const [pscInput, setPscInput] = useState('');
  const [agencyInput, setAgencyInput] = useState('');
  const [isSavingPrefs, setIsSavingPrefs] = useState(false);

  const fetchWorkspaceSettings = useCallback(async () => {
    if (!authToken) return;
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/workspace/settings`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOrganization(data.organization);
        setTeam(data.team || []);
        setLinkedConsultant(data.linked_consultant);
      } else {
        console.error('Failed to fetch workspace settings');
      }
    } catch (error) {
      console.error('Error fetching workspace settings:', error);
    } finally {
      setIsLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    fetchWorkspaceSettings();
    fetchSamGovStatus();
    fetchSentinelPrefs();
  }, [fetchWorkspaceSettings]);

  // Fetch Sentinel Preferences from org profile
  const fetchSentinelPrefs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pursuits/organization-profile`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSentinelPrefs({
          target_naics_codes: data.target_naics_codes || [],
          target_psc_codes: data.target_psc_codes || [],
          preferred_agencies: data.preferred_agencies || []
        });
      }
    } catch (error) {
      console.error('Error fetching sentinel preferences:', error);
    }
  };

  // Save Sentinel Preferences
  const handleSaveSentinelPrefs = async () => {
    setIsSavingPrefs(true);
    try {
      const response = await fetch(`${API_URL}/api/pursuits/organization-profile`, {
        method: 'PATCH',
        headers: { 
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sentinelPrefs)
      });
      
      if (response.ok) {
        toast.success('Sentinel preferences saved!');
      } else {
        toast.error('Failed to save preferences');
      }
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setIsSavingPrefs(false);
    }
  };

  // Fetch SAM.gov integration status
  const fetchSamGovStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sentinel/integrations/sam-gov`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSamGovStatus(data);
      }
    } catch (error) {
      console.error('Error fetching SAM.gov status:', error);
    }
  };

  // Save SAM.gov API key
  const handleSaveApiKey = async () => {
    if (!samGovApiKey.trim()) {
      toast.error('Please enter an API key');
      return;
    }
    
    setIsSavingApiKey(true);
    try {
      const response = await fetch(`${API_URL}/api/sentinel/integrations/sam-gov`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ sam_gov_api_key: samGovApiKey })
      });
      
      if (response.ok) {
        toast.success('SAM.gov API key saved successfully!');
        setSamGovApiKey('');
        setShowApiKey(false);
        fetchSamGovStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save API key');
      }
    } catch (error) {
      toast.error('Failed to save API key');
    } finally {
      setIsSavingApiKey(false);
    }
  };

  // Test SAM.gov connection
  const handleTestConnection = async () => {
    setIsTestingConnection(true);
    try {
      const response = await fetch(`${API_URL}/api/sentinel/integrations/sam-gov/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          toast.success('Connection successful! SAM.gov API is working.');
        } else {
          toast.error(data.error || 'Connection test failed');
        }
      } else {
        toast.error('Connection test failed');
      }
    } catch (error) {
      toast.error('Failed to test connection');
    } finally {
      setIsTestingConnection(false);
    }
  };

  // Remove SAM.gov API key
  const handleRemoveApiKey = async () => {
    if (!window.confirm('Are you sure you want to remove the SAM.gov API key? This will disable live opportunity scanning.')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/sentinel/integrations/sam-gov`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        toast.success('API key removed. Sentinel will use demo mode.');
        fetchSamGovStatus();
      } else {
        toast.error('Failed to remove API key');
      }
    } catch (error) {
      toast.error('Failed to remove API key');
    }
  };

  // Search for consultants with debounce
  useEffect(() => {
    if (consultantSearch.length < 2) {
      setSearchResults([]);
      return;
    }
    
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await fetch(
          `${API_URL}/api/consultants/search?q=${encodeURIComponent(consultantSearch)}`,
          { headers: { 'Authorization': `Bearer ${authToken}` } }
        );
        if (response.ok) {
          const data = await response.json();
          setSearchResults(data);
        }
      } catch (error) {
        console.error('Error searching consultants:', error);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [consultantSearch, authToken]);

  const handleLinkConsultant = async (consultantOrgId) => {
    setIsLinking(true);
    try {
      const response = await fetch(`${API_URL}/api/consultants/link?consultant_org_id=${consultantOrgId}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Successfully linked with consultant!');
        setConsultantSearch('');
        setSearchResults([]);
        fetchWorkspaceSettings(); // Refresh to show linked consultant
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to link with consultant');
      }
    } catch (error) {
      console.error('Error linking consultant:', error);
      toast.error('Failed to link with consultant');
    } finally {
      setIsLinking(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getRoleLabel = (role) => {
    const labels = {
      'CONTRACTOR_ADMIN': 'Contractor Admin',
      'BID_MANAGER': 'Bid Manager',
      'REVIEWER': 'Reviewer',
      'CONSULTANT': 'Consultant',
      'CONSULTANT_ASSOCIATE': 'Associate'
    };
    return labels[role] || role;
  };

  if (isLoading) {
    return (
      <div data-testid="workspace-settings-shell" className="min-h-screen bg-slate-50">
        <PageHeader 
          title="Workspace Settings"
          description="Manage your workspace configuration, team members, and consultant access"
        />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="workspace-settings-shell" className="min-h-screen bg-slate-50">
      <PageHeader 
        title="Workspace Settings"
        description="Manage your workspace configuration, team members, and consultant access"
        action={
          <Button
            variant="outline"
            onClick={fetchWorkspaceSettings}
            disabled={isLoading}
            className="border-slate-200"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />
      
      <div className="p-8">
        <div className="max-w-7xl mx-auto space-y-6">
        <Tabs defaultValue="company" className="w-full">
          <TabsList className="mb-6 bg-slate-100">
            <TabsTrigger value="company" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-600" data-testid="settings-company-tab">
              <Building2 className="w-4 h-4 mr-2" />
              Company Profile
            </TabsTrigger>
            <TabsTrigger value="library" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-600" data-testid="settings-library-tab">
              <BookOpen className="w-4 h-4 mr-2" />
              Library
            </TabsTrigger>
            <TabsTrigger value="team" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-600" data-testid="settings-team-tab">
              <Users className="w-4 h-4 mr-2" />
              Team Management
            </TabsTrigger>
            <TabsTrigger value="consultant" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-600" data-testid="settings-consultant-tab">
              <Briefcase className="w-4 h-4 mr-2" />
              Consultant Access
            </TabsTrigger>
            {sentinelEnabled && (
              <TabsTrigger value="integrations" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-600" data-testid="settings-integrations-tab">
                <Plug className="w-4 h-4 mr-2" />
                Integrations
              </TabsTrigger>
            )}
            <TabsTrigger value="billing" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-600" data-testid="settings-billing-tab">
              <Sparkles className="w-4 h-4 mr-2" />
              Billing
            </TabsTrigger>
            <TabsTrigger value="ai-config" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-600" data-testid="settings-ai-config-tab">
              <Zap className="w-4 h-4 mr-2" />
              AI Configuration
            </TabsTrigger>
          </TabsList>

          {/* TEAM MANAGEMENT TAB - Primary for Contractor Admin */}
          <TabsContent value="team">
            <div className="space-y-6">
              {/* Invite New Member */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <UserPlus className="w-5 h-5 text-teal-700" />
                    Invite Team Member
                  </CardTitle>
                  <CardDescription>
                    Add internal team members to your workspace
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end gap-4">
                    <div className="flex-1 space-y-2">
                      <Label htmlFor="invite-email">Email Address</Label>
                      <Input 
                        id="invite-email"
                        type="email"
                        placeholder="colleague@company.com"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        className="border-slate-200"
                        data-testid="invite-email-input"
                      />
                    </div>
                    <div className="w-48 space-y-2">
                      <Label htmlFor="invite-role">Role</Label>
                      <Select value={inviteRole} onValueChange={setInviteRole}>
                        <SelectTrigger className="border-slate-200" data-testid="invite-role-select">
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bid_manager">Bid Manager</SelectItem>
                          <SelectItem value="reviewer">Reviewer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      className="bg-teal-700 hover:bg-teal-800 text-white"
                      disabled={!inviteEmail || !inviteRole}
                      data-testid="send-invite-btn"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Send Invite
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    Note: Only Contractor Admins can invite new members. Consultants cannot add/remove users from your organization.
                  </p>
                </CardContent>
              </Card>

              {/* Team Members Table */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Workspace Team ({team.length} members)
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="grid grid-cols-6 gap-4 px-6 py-3 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    <div className="col-span-2">Member</div>
                    <div>Role</div>
                    <div>Status</div>
                    <div>Last Login</div>
                    <div>Actions</div>
                  </div>
                  {team.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16">
                      <Users className="w-12 h-12 text-slate-300 mb-3" />
                      <p className="text-slate-500 text-sm">No team members found</p>
                    </div>
                  ) : (
                    team.map((member) => (
                      <div key={member.id} className="grid grid-cols-6 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 transition-colors">
                        <div className="col-span-2 flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-slate-600">{(member.name || member.email || '?').charAt(0).toUpperCase()}</span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-900">{member.name || member.email}</p>
                              {member.is_owner && (
                                <Badge variant="outline" className="text-xs border-amber-300 text-amber-700">Owner</Badge>
                              )}
                            </div>
                            <p className="text-xs text-slate-500">{member.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <Badge variant="secondary" className={
                            member.role === 'CONTRACTOR_ADMIN' ? 'bg-teal-100 text-teal-700' :
                            member.role === 'BID_MANAGER' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-600'
                          }>
                            {getRoleLabel(member.role)}
                          </Badge>
                        </div>
                        <div className="flex items-center">
                          {member.is_active !== false ? (
                            <span className="flex items-center gap-1.5 text-sm text-emerald-600">
                              <CheckCircle2 className="w-4 h-4" />
                              Active
                            </span>
                          ) : (
                            <span className="flex items-center gap-1.5 text-sm text-amber-600">
                              <Clock className="w-4 h-4" />
                              Pending
                            </span>
                          )}
                        </div>
                        <div className="flex items-center text-sm text-slate-500">
                          {member.last_login ? formatDate(member.last_login) : 'Never'}
                        </div>
                        <div className="flex items-center gap-2">
                          {!member.is_owner && (
                            <>
                              <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-900" data-testid={`edit-member-${member.id}`}>
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" data-testid={`remove-member-${member.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* CONSULTANT ACCESS TAB */}
          <TabsContent value="consultant">
            <div className="space-y-6">
              {/* Current Linked Consultant */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Briefcase className="w-5 h-5 text-blue-700" />
                    Linked Consultant
                  </CardTitle>
                  <CardDescription>
                    External consultant with access to your workspace
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {linkedConsultant ? (
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Briefcase className="w-6 h-6 text-blue-700" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{linkedConsultant.name}</p>
                            <p className="text-sm text-slate-600">{linkedConsultant.consultancy}</p>
                            <p className="text-xs text-slate-500">{linkedConsultant.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 mb-2">
                            {linkedConsultant.status}
                          </Badge>
                          <p className="text-xs text-slate-500">Linked: {formatDate(linkedConsultant.linked_date)}</p>
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t border-blue-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-slate-700">Access Level</p>
                            <p className="text-xs text-slate-500">Consultant can view and manage pursuits, but cannot modify team settings</p>
                          </div>
                          <Badge variant="secondary">Bid Manager</Badge>
                        </div>
                      </div>
                      
                      <div className="mt-4 flex items-center gap-3">
                        <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-100">
                          <Edit2 className="w-4 h-4 mr-2" />
                          Change Access
                        </Button>
                        <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid="revoke-consultant-btn">
                          <XCircle className="w-4 h-4 mr-2" />
                          Revoke Access
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <Briefcase className="w-12 h-12 text-slate-300 mb-3" />
                      <p className="text-slate-500 text-sm mb-1">No consultant linked</p>
                      <p className="text-slate-400 text-xs">Search for and invite a consultant to collaborate on your pursuits</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Invite New Consultant */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <UserPlus className="w-5 h-5 text-teal-700" />
                    {linkedConsultant ? 'Link Another Consultant' : 'Find & Link Consultant'}
                  </CardTitle>
                  <CardDescription>
                    Search for registered consultancies to collaborate on your pursuits
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="consultant-search">Search Consultancies</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input 
                          id="consultant-search"
                          type="text"
                          placeholder="Type consultancy name (e.g., MNGC, PKG, Federal...)"
                          className="border-slate-200 pl-10"
                          value={consultantSearch}
                          onChange={(e) => setConsultantSearch(e.target.value)}
                          data-testid="consultant-search-input"
                        />
                        {isSearching && (
                          <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
                        )}
                      </div>
                      <p className="text-xs text-slate-400">
                        Type at least 2 characters to search our directory
                      </p>
                    </div>
                    
                    {/* Search Results */}
                    {searchResults.length > 0 && (
                      <div className="border border-slate-200 rounded-lg overflow-hidden">
                        <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                          <p className="text-xs font-medium text-slate-600">{searchResults.length} consultancies found</p>
                        </div>
                        <div className="max-h-64 overflow-y-auto">
                          {searchResults.map((consultancy) => (
                            <div 
                              key={consultancy.id} 
                              className="p-4 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <Briefcase className="w-5 h-5 text-blue-700" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-slate-900">{consultancy.name}</p>
                                    {consultancy.consultant_name && (
                                      <p className="text-xs text-slate-500">
                                        Contact: {consultancy.consultant_name}
                                        {consultancy.consultant_email && ` (${consultancy.consultant_email})`}
                                      </p>
                                    )}
                                    <p className="text-xs text-slate-400">
                                      {consultancy.client_count} active client{consultancy.client_count !== 1 ? 's' : ''}
                                    </p>
                                  </div>
                                </div>
                                <Button 
                                  size="sm"
                                  className="bg-teal-700 hover:bg-teal-800 text-white"
                                  onClick={() => handleLinkConsultant(consultancy.id)}
                                  disabled={isLinking}
                                  data-testid={`link-consultant-${consultancy.id}`}
                                >
                                  {isLinking ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <Link2 className="w-4 h-4 mr-1" />
                                      Link
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {consultantSearch.length >= 2 && searchResults.length === 0 && !isSearching && (
                      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 text-center">
                        <p className="text-sm text-slate-500">No consultancies found matching "{consultantSearch}"</p>
                        <p className="text-xs text-slate-400 mt-1">Try a different search term</p>
                      </div>
                    )}
                    
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <h4 className="text-sm font-medium text-slate-700 mb-2">Consultant Access Includes:</h4>
                      <ul className="text-xs text-slate-500 space-y-1">
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-3 h-3 text-teal-600" />
                          Go/No-Go Analysis tools
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-3 h-3 text-teal-600" />
                          Pursuit Pipeline management
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-3 h-3 text-teal-600" />
                          Read access to Knowledge Base
                        </li>
                        <li className="flex items-center gap-2">
                          <XCircle className="w-3 h-3 text-red-400" />
                          <span className="text-slate-400">Cannot add/remove your team members</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <XCircle className="w-3 h-3 text-red-400" />
                          <span className="text-slate-400">Cannot access workspace settings</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* INTEGRATIONS TAB */}
          {sentinelEnabled && (
          <TabsContent value="integrations">
            <div className="space-y-6">
              {/* SAM.gov Integration */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                        <Radar className="w-5 h-5 text-teal-700" />
                      </div>
                      <div>
                        <CardTitle className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          SAM.gov Integration
                        </CardTitle>
                        <CardDescription>
                          Connect to SAM.gov for live opportunity scanning
                        </CardDescription>
                      </div>
                    </div>
                    <Badge className={samGovStatus?.has_api_key ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                      {samGovStatus?.has_api_key ? 'Connected' : 'Demo Mode'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Status Info */}
                  {samGovStatus && (
                    <div className="grid grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Status</p>
                        <p className="text-sm font-medium text-slate-900 capitalize">{samGovStatus.status}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Daily Usage</p>
                        <p className="text-sm font-medium text-slate-900">
                          {samGovStatus.daily_usage} / {samGovStatus.daily_limit} calls
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Last Scan</p>
                        <p className="text-sm font-medium text-slate-900">
                          {samGovStatus.last_sync ? formatDate(samGovStatus.last_sync) : 'Never'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Next Scheduled</p>
                        <p className="text-sm font-medium text-slate-900">
                          {samGovStatus.next_scan ? formatDate(samGovStatus.next_scan) : 'Pending first scan'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* API Key Input */}
                  {!samGovStatus?.has_api_key ? (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="sam-gov-api-key">SAM.gov API Key</Label>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Input 
                              id="sam-gov-api-key"
                              type={showApiKey ? 'text' : 'password'}
                              placeholder="Enter your SAM.gov API key"
                              value={samGovApiKey}
                              onChange={(e) => setSamGovApiKey(e.target.value)}
                              className="border-slate-200 pr-10"
                              data-testid="sam-gov-api-key-input"
                            />
                            <button
                              type="button"
                              onClick={() => setShowApiKey(!showApiKey)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            >
                              {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                          <Button 
                            onClick={handleSaveApiKey}
                            disabled={isSavingApiKey || !samGovApiKey.trim()}
                            className="bg-teal-700 hover:bg-teal-800 text-white"
                            data-testid="save-api-key-btn"
                          >
                            {isSavingApiKey ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Key className="w-4 h-4 mr-2" />
                                Save Key
                              </>
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-slate-500">
                          Your API key is encrypted and stored securely.
                        </p>
                      </div>

                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <h4 className="text-sm font-medium text-blue-900">How to get a SAM.gov API Key</h4>
                            <ol className="mt-2 text-xs text-blue-800 space-y-1 list-decimal list-inside">
                              <li>Visit <a href="https://api.sam.gov" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">api.sam.gov</a></li>
                              <li>Create an account or sign in</li>
                              <li>Navigate to "API Keys" in your account settings</li>
                              <li>Generate a new public API key for "Opportunities API"</li>
                              <li>Copy and paste the key above</li>
                            </ol>
                            <a 
                              href="https://api.sam.gov" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 mt-2 text-xs text-blue-700 hover:text-blue-800 font-medium"
                            >
                              Go to SAM.gov API Portal
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center gap-3">
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                          <div>
                            <p className="text-sm font-medium text-green-900">API Key Configured</p>
                            <p className="text-xs text-green-700">Your SAM.gov integration is active</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline"
                            size="sm"
                            onClick={handleTestConnection}
                            disabled={isTestingConnection}
                            className="border-green-300 text-green-700 hover:bg-green-100"
                            data-testid="test-connection-btn"
                          >
                            {isTestingConnection ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <RefreshCw className="w-4 h-4 mr-1" />
                                Test
                              </>
                            )}
                          </Button>
                          <Button 
                            variant="outline"
                            size="sm"
                            onClick={handleRemoveApiKey}
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            data-testid="remove-api-key-btn"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Remove
                          </Button>
                        </div>
                      </div>

                      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <h4 className="text-sm font-medium text-slate-700 mb-2">Integration Features</h4>
                        <ul className="text-xs text-slate-600 space-y-1">
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-teal-600" />
                            Automatic opportunity scanning every 4 hours
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-teal-600" />
                            Real-time matching against your NAICS/PSC codes
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-teal-600" />
                            Alert notifications for high-value opportunities
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-teal-600" />
                            One-click pursuit creation from matches
                          </li>
                        </ul>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Sentinel Preferences - NAICS/PSC Codes */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                      <Target className="w-5 h-5 text-teal-700" />
                    </div>
                    <div>
                      <CardTitle className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Sentinel Preferences
                      </CardTitle>
                      <CardDescription>
                        Configure NAICS and PSC codes for opportunity matching
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Target NAICS Codes */}
                  <div className="space-y-3">
                    <Label className="flex items-center gap-2">
                      <Hash className="w-3 h-3" />
                      Target NAICS Codes
                    </Label>
                    <p className="text-xs text-slate-500">
                      Add NAICS codes that match your core capabilities (e.g., 541512 for Computer Systems Design)
                    </p>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter NAICS code (e.g., 541512)"
                        value={naicsInput}
                        onChange={(e) => setNaicsInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && naicsInput.trim()) {
                            e.preventDefault();
                            if (!sentinelPrefs.target_naics_codes.includes(naicsInput.trim())) {
                              setSentinelPrefs(prev => ({
                                ...prev,
                                target_naics_codes: [...prev.target_naics_codes, naicsInput.trim()]
                              }));
                            }
                            setNaicsInput('');
                          }
                        }}
                        className="flex-1"
                        data-testid="naics-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          if (naicsInput.trim() && !sentinelPrefs.target_naics_codes.includes(naicsInput.trim())) {
                            setSentinelPrefs(prev => ({
                              ...prev,
                              target_naics_codes: [...prev.target_naics_codes, naicsInput.trim()]
                            }));
                            setNaicsInput('');
                          }
                        }}
                        className="border-teal-200 text-teal-700 hover:bg-teal-50"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 min-h-[32px]">
                      {sentinelPrefs.target_naics_codes.map((code) => (
                        <Badge 
                          key={code} 
                          className="bg-teal-100 text-teal-700 border border-teal-200 px-3 py-1 text-sm font-mono"
                        >
                          {code}
                          <button
                            type="button"
                            onClick={() => setSentinelPrefs(prev => ({
                              ...prev,
                              target_naics_codes: prev.target_naics_codes.filter((c) => c !== code)
                            }))}
                            className="ml-2 hover:text-teal-900"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                      {sentinelPrefs.target_naics_codes.length === 0 && (
                        <span className="text-xs text-slate-400 italic">No NAICS codes added yet</span>
                      )}
                    </div>
                  </div>

                  {/* Target PSC Codes */}
                  <div className="space-y-3">
                    <Label className="flex items-center gap-2">
                      <FileText className="w-3 h-3" />
                      Target PSC Codes
                    </Label>
                    <p className="text-xs text-slate-500">
                      Add Product Service Codes for more precise matching (e.g., D310 for IT Cyber Security)
                    </p>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter PSC code (e.g., D310)"
                        value={pscInput}
                        onChange={(e) => setPscInput(e.target.value.toUpperCase().slice(0, 4))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && pscInput.trim()) {
                            e.preventDefault();
                            if (!sentinelPrefs.target_psc_codes.includes(pscInput.trim())) {
                              setSentinelPrefs(prev => ({
                                ...prev,
                                target_psc_codes: [...prev.target_psc_codes, pscInput.trim()]
                              }));
                            }
                            setPscInput('');
                          }
                        }}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          if (pscInput.trim() && !sentinelPrefs.target_psc_codes.includes(pscInput.trim())) {
                            setSentinelPrefs(prev => ({
                              ...prev,
                              target_psc_codes: [...prev.target_psc_codes, pscInput.trim()]
                            }));
                            setPscInput('');
                          }
                        }}
                        className="border-teal-200 text-teal-700 hover:bg-teal-50"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 min-h-[32px]">
                      {sentinelPrefs.target_psc_codes.map((code) => (
                        <Badge 
                          key={code} 
                          className="bg-purple-100 text-purple-700 border border-purple-200 px-3 py-1 text-sm font-mono"
                        >
                          {code}
                          <button
                            type="button"
                            onClick={() => setSentinelPrefs(prev => ({
                              ...prev,
                              target_psc_codes: prev.target_psc_codes.filter((c) => c !== code)
                            }))}
                            className="ml-2 hover:text-purple-900"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                      {sentinelPrefs.target_psc_codes.length === 0 && (
                        <span className="text-xs text-slate-400 italic">No PSC codes added yet</span>
                      )}
                    </div>
                  </div>

                  {/* Preferred Agencies */}
                  <div className="space-y-3">
                    <Label className="flex items-center gap-2">
                      <Building2 className="w-3 h-3" />
                      Preferred Agencies (Optional)
                    </Label>
                    <p className="text-xs text-slate-500">
                      Add agency abbreviations to prioritize (e.g., DOD, DHS, VA)
                    </p>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter agency (e.g., DOD)"
                        value={agencyInput}
                        onChange={(e) => setAgencyInput(e.target.value.toUpperCase())}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && agencyInput.trim()) {
                            e.preventDefault();
                            if (!sentinelPrefs.preferred_agencies.includes(agencyInput.trim())) {
                              setSentinelPrefs(prev => ({
                                ...prev,
                                preferred_agencies: [...prev.preferred_agencies, agencyInput.trim()]
                              }));
                            }
                            setAgencyInput('');
                          }
                        }}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          if (agencyInput.trim() && !sentinelPrefs.preferred_agencies.includes(agencyInput.trim())) {
                            setSentinelPrefs(prev => ({
                              ...prev,
                              preferred_agencies: [...prev.preferred_agencies, agencyInput.trim()]
                            }));
                            setAgencyInput('');
                          }
                        }}
                        className="border-teal-200 text-teal-700 hover:bg-teal-50"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 min-h-[32px]">
                      {sentinelPrefs.preferred_agencies.map((agency, idx) => (
                        <Badge 
                          key={idx} 
                          className="bg-slate-100 text-slate-700 border border-slate-200 px-3 py-1 text-sm"
                        >
                          {agency}
                          <button
                            type="button"
                            onClick={() => setSentinelPrefs(prev => ({
                              ...prev,
                              preferred_agencies: prev.preferred_agencies.filter((_, i) => i !== idx)
                            }))}
                            className="ml-2 hover:text-slate-900"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                      {sentinelPrefs.preferred_agencies.length === 0 && (
                        <span className="text-xs text-slate-400 italic">No agencies specified - will match all agencies</span>
                      )}
                    </div>
                  </div>

                  {/* Save Preferences Button */}
                  <Button 
                    onClick={handleSaveSentinelPrefs}
                    disabled={isSavingPrefs}
                    className="w-full bg-teal-700 hover:bg-teal-800 text-white"
                  >
                    {isSavingPrefs ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                    )}
                    Save Preferences
                  </Button>

                  {/* Match Scoring Info */}
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <h4 className="text-sm font-medium text-slate-700 mb-2">How Matching Works</h4>
                    <ul className="text-xs text-slate-600 space-y-1">
                      <li className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-amber-400"></span>
                        <strong>Gold Match (50%):</strong> NAICS + PSC code match
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-slate-400"></span>
                        <strong>Silver Match (40%):</strong> NAICS + preferred agency match
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-orange-400"></span>
                        <strong>Bronze Match (30%):</strong> NAICS code only
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* v3.18.23 — GSA eBuy Sentinel SIN profile */}
              <EbuyProfilePanel />

              {/* Future Integrations Placeholder */}
              <Card className="border-slate-200 shadow-sm border-dashed">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-slate-400 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Plug className="w-5 h-5" />
                    More Integrations Coming Soon
                  </CardTitle>
                  <CardDescription>
                    GovWin, FPDS, USA Spending, and more
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          </TabsContent>
          )}

          {/* GENERAL TAB */}
          <TabsContent value="company">
            <div className="space-y-6">
              {/* Full company profile — same fields a consultant maintains per client */}
              <OrgCompanyProfile orgId={organization?.id} title="Company Profile" />

              {/* Plan & capacity (retained from the old General tab) */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Sparkles className="w-5 h-5 text-teal-700" />
                    Plan &amp; Capacity
                  </CardTitle>
                  <CardDescription>Subscription, pursuit slots, and workspace ID</CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Subscription Plan</Label>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-sm">
                        {organization?.subscription_plan?.replace('_', ' ') || 'SMALL BUSINESS'}
                      </Badge>
                      <Button
                        variant="outline" size="sm"
                        onClick={() => {
                          const ws = window.location.pathname.split('/')[1] || '';
                          window.location.href = ws ? `/${ws}/billing/subscribe` : '/billing/subscribe';
                        }}
                        className="border-teal-200 text-teal-700 hover:bg-teal-50 h-7 text-xs"
                        data-testid="upgrade-plan-btn"
                      >
                        Upgrade / Change
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Pursuit Capacity</Label>
                    <p className="text-sm text-slate-600">
                      {organization?.active_pursuits || 0} / {organization?.pursuit_capacity || 2} active
                      {organization?.purchased_slots > 0 && (
                        <span className="ml-2 text-xs text-emerald-700">(+{organization.purchased_slots} purchased)</span>
                      )}
                    </p>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => setIsSlotTopUpOpen(true)}
                      className="border-teal-200 text-teal-700 hover:bg-teal-50"
                      data-testid="open-slot-topup-btn"
                    >
                      <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Buy More Slots
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="workspace-id">Workspace ID</Label>
                    <Input id="workspace-id" value={organization?.slug || organization?.id || ''}
                      className="border-slate-200 bg-slate-50" disabled data-testid="workspace-id-input" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* LIBRARY TAB — Certs, Past Performance, Key Personnel, Winning Proposals
              (same KB a consultant maintains per client, scoped to own org) */}
          <TabsContent value="library">
            <OrgLibrary orgId={organization?.id} allowPromote={false} />
          </TabsContent>

          {/* BILLING TAB — surfaces the Subscription + Slots controls in a discoverable place */}
          <TabsContent value="billing">
            <div className="space-y-6">
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2"
                             style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Sparkles className="w-5 h-5 text-teal-700" />
                    Subscription & Capacity
                  </CardTitle>
                  <CardDescription>
                    Manage your plan, seats, and pursuit slot capacity
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Current Plan</Label>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-sm">
                          {organization?.subscription_plan?.replace('_', ' ') || 'SMALL BUSINESS'}
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const ws = window.location.pathname.split('/')[1] || '';
                            window.location.href = ws ? `/${ws}/billing/subscribe` : '/billing/subscribe';
                          }}
                          className="border-teal-200 text-teal-700 hover:bg-teal-50 h-7 text-xs"
                          data-testid="upgrade-plan-btn-billing"
                        >
                          Upgrade / Change
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500">
                        Change plan anytime — changes apply on the next renewal.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Pursuit Capacity</Label>
                      <p className="text-sm text-slate-600">
                        {organization?.active_pursuits || 0} / {organization?.pursuit_capacity || 2} active
                        {organization?.purchased_slots > 0 && (
                          <span className="ml-2 text-xs text-emerald-700">
                            (+{organization.purchased_slots} purchased)
                          </span>
                        )}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsSlotTopUpOpen(true)}
                        className="border-teal-200 text-teal-700 hover:bg-teal-50"
                        data-testid="open-slot-topup-btn-billing"
                      >
                        <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                        Buy More Slots
                      </Button>
                    </div>
                  </div>

                  <div className="border-t border-slate-200 pt-5">
                    <Label className="mb-2 block">Self-Serve Billing</Label>
                    <p className="text-sm text-slate-600 mb-3">
                      Update your card, view past invoices, or cancel directly in Stripe&apos;s secure portal.
                    </p>
                    <CustomerPortalButton testId="open-customer-portal-btn-billing" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* AI Configuration (BYOM) — its own tab, outside the
              Sentinel-gated Integrations tab so contractors can find it
              even when Sentinel hasn't been turned on for them. The card
              itself talks to /api/llm/org-config which is role-gated to
              Contractor Admin / Super Admin. */}
          <TabsContent value="ai-config">
            <div className="space-y-6 max-w-4xl">
              <OrgAIConfigurationCard authToken={authToken} isSuperAdmin={false} />
            </div>
          </TabsContent>
        </Tabs>
        </div>
      </div>
      <SlotTopUpModal open={isSlotTopUpOpen} onOpenChange={setIsSlotTopUpOpen} />
    </div>
  );
}
